import { InputHTMLAttributes, forwardRef } from 'react'

import { inputId } from 'aem-libs-tests/src/constants/inputId'
import { createTestId } from 'aem-libs-tests/src/utility/createTestId'

import { CommonInputWrapper } from '../CommonInputWrapper'
import * as InputStyles from '../input.styled'
import { CommonInputProps } from '../types'
import { useFieldError } from '../useFieldError'
import { useFieldMeta } from '../useFieldMeta'
import { useFieldValue } from '../useFieldValue'
import { ValidationMessage } from '../validation-message'


interface HtmlInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'width' | 'pattern'> {
  value: string
  onChangeComplete?: (value: string) => void
}

export interface TextFieldProps extends HtmlInputProps, CommonInputProps {
  outerId?: string
  pattern?: RegExp
  isActive?: boolean
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      className,
      width,
      onChangeComplete,
      outerId,
      onChange,
      value,
      type = 'text',
      label,
      validationMessage,
      hideValidation,
      tip,
      trackingDescription,
      responsive,
      pattern,
      isActive,
      testId,
      ...inputProps
    },
    ref
  ) => {
    const { currentValue, handleChange, handleBlur, handleKeyPress } = useFieldValue<HTMLInputElement, string>(
      value,
      {
        onChangeComplete,
        onChange,
        onKeyPress: inputProps.onKeyPress,
        onBlur: inputProps.onBlur
      },
      {
        description: trackingDescription ?? label,
        id: inputProps.id
      },
      {
        pattern
      }
    )

    const { showError, ariaErrorProps, errorMessage } = useFieldError(inputProps.id, validationMessage, {
      hideValidation
    })
    const fieldProps = useFieldMeta(inputProps.id, { tip }, ariaErrorProps)
    const usedTestId = testId ?? 'textfield'

    return (
      <CommonInputWrapper id={inputProps.id} htmlFor={inputProps.id} label={label} tip={tip} testId={usedTestId}>
        <InputStyles.InputContainer
          id={outerId}
          className={className}
          width={width}
          responsive={responsive}
          isActive={isActive}
        >
          <InputStyles.Input
            {...inputProps}
            data-testid={createTestId(usedTestId, inputId.input)}
            ref={ref}
            type={type}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyPress}
            value={currentValue}
            {...fieldProps}
          />
        </InputStyles.InputContainer>
        {showError && <ValidationMessage id={inputProps.id} message={errorMessage} testId={usedTestId} label={label} />}
      </CommonInputWrapper>
    )
  }
)

TextField.displayName = 'TextField'
